import { keepPreviousData, useInfiniteQuery, useQuery, type QueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'
import { safe } from '../../lib/prefs.ts'
import type { Verse } from '../ayat/ayat.ts'

/** Mirrors apps/api/src/routes/quran.ts and `GET/PUT /api/me/reading`. */

export type SurahInfo = { n: number; nama: string; latin: string; namaAr: string; arti: string; tempat: string; jumlah: number }
export type JuzStart = { n: number; surah: number; ayah: number }
type QuranIndex = { ok: true; surahs: SurahInfo[]; juz?: JuzStart[] }
export type SurahPage = { ok: true; surah: SurahInfo; from: number; to: number; bismillah: { ar: string; terjemah: string } | null; verses: Verse[] }
export type Reading = { surah: number; ayah: number }

export const PAGE = 40

const quranIndex = () => api<QuranIndex>('GET', '/api/quran')
export const useSurahs = () => useQuery({ queryKey: ['surahs'], queryFn: quranIndex, staleTime: Infinity, select: (r) => r.surahs })
export const useJuz = () => useQuery({ queryKey: ['surahs'], queryFn: quranIndex, staleTime: Infinity, select: (r) => r.juz ?? [] })

/** First verse of the page that contains `ayah` (pages are 1–40, 41–80, …). */
export const pageStart = (ayah: number) => ayah - ((ayah - 1) % PAGE)

type Range = { from: number; to?: number }
export const useSurahPages = (n: number, start: number) =>
  useInfiniteQuery({
    queryKey: ['surah', n, start],
    queryFn: ({ pageParam }: { pageParam: Range }) =>
      api<SurahPage>('GET', `/api/quran/${n}?from=${pageParam.from}${pageParam.to ? `&to=${pageParam.to}` : ''}`),
    initialPageParam: { from: start } as Range,
    getNextPageParam: (last) => (last.to < last.surah.jumlah ? { from: last.to + 1 } : undefined),
    getPreviousPageParam: (first) => (first.from > 1 ? { from: Math.max(1, first.from - PAGE), to: first.from - 1 } : undefined),
    staleTime: Infinity,
  })

export const fetchSurahAudio = (n: number) => api<{ ok: true; audio: string[] }>('GET', `/api/quran/${n}/audio`)

const LAST_KEY = 'iqp_last_read'
export const localReading = (): Reading | null => {
  try {
    const r = JSON.parse(safe.get(LAST_KEY) ?? 'null') as Reading | null
    return r && Number.isInteger(r.surah) && Number.isInteger(r.ayah) ? r : null
  } catch { return null }
}
/**
 * "Lanjut baca" = the verse marked with "Tandai terakhir baca" (set by hand, never by scrolling). The account copy is the
 * truth (other devices see it); the device copy only shows something at once while it loads or offline.
 */
export const READING_KEY = ['reading'] as const
const keepLocal = (r: Reading | null) => (r ? safe.set(LAST_KEY, JSON.stringify(r)) : safe.remove(LAST_KEY))
export const useReading = () =>
  useQuery({
    queryKey: READING_KEY,
    queryFn: async () => {
      const r = (await api<{ ok: true; reading: Reading | null }>('GET', '/api/me/reading')).reading
      const mark = r ? { surah: r.surah, ayah: r.ayah } : null
      keepLocal(mark)
      return mark
    },
    placeholderData: localReading,
  })

/** Set (or with null remove) the marker: on screen at once, back to what it was if the server says no. */
export async function setReadingMark(qc: QueryClient, r: Reading | null) {
  const prev = qc.getQueryData<Reading | null>(READING_KEY) ?? null
  qc.setQueryData(READING_KEY, r)
  keepLocal(r)
  try {
    if (r) await api<{ ok: true }>('PUT', '/api/me/reading', r)
    else await api<{ ok: true }>('POST', '/api/me/reading/hapus')
    return null
  } catch (e) {
    qc.setQueryData(READING_KEY, prev)
    keepLocal(prev)
    return e
  }
}

/** Lowercase, no diacritics, letters/digits only: "Āli ‘Imrān" → "aliimran", so "al imran" or "imran" match. */
export const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]/g, '')

export function matches(s: SurahInfo, q: string) {
  const k = norm(q)
  if (!k) return true
  if (/^\d+$/.test(k)) return String(s.n) === k || String(s.n).startsWith(k)
  return [s.nama, s.latin, s.arti].some((x) => norm(x).includes(k))
}

/** Popular surahs for the quick chips. */
export const POPULAR = [18, 36, 55, 56, 67, 1]

/** "albaqarah" → "baqarah", "asysyarh" → "syarh": the Arabic article left out when comparing names. */
const bare = (x: string) => x.replace(/^(asy|ats|adz|al|an|ar|as|at|ad|az)(?=[a-z]{3})/, '')

/** Surahs matching a name query, best first: exact name, then names starting with it, then the rest (Qur'an order). */
export function rankSurahs(list: SurahInfo[], q: string) {
  const k = norm(q)
  if (!k) return list
  const hit = list.filter((s) => matches(s, q))
  if (/^\d+$/.test(k)) return hit
  const score = (s: SurahInfo) => {
    const names = [s.nama, s.latin].map(norm)
    return names.some((x) => x === k || bare(x) === k) ? 0 : names.some((x) => x.startsWith(k) || bare(x).startsWith(k)) ? 1 : 2
  }
  return hit.map((s) => [score(s), s] as const).sort((a, b) => a[0] - b[0]).map(([, s]) => s)
}

export type RefHit = { surah: SurahInfo; ayah: number | null; tooFar?: boolean }

/**
 * "2:255", "2 255", "QS 2:255", "baqarah 255", "al-kahfi:10" → that verse; "36" or "QS 36" → that surah. A verse number
 * past the end of the surah is flagged (`tooFar`). null when the query is not a reference.
 */
export function parseRef(raw: string, list: SurahInfo[]): RefHit | null {
  const q = raw.trim().toLowerCase().replace(/^(qs|q\.s)\.?\s*/, '').replace(/\s+/g, ' ')
  if (!q) return null
  const at = (s: SurahInfo | undefined, a?: string): RefHit | null => {
    if (!s) return null
    if (a === undefined) return { surah: s, ayah: null }
    const n = Number(a)
    return n < 1 ? null : n > s.jumlah ? { surah: s, ayah: s.jumlah, tooFar: true } : { surah: s, ayah: n }
  }
  let m = /^(\d{1,3})(?:\s*[:.\s/]\s*(\d{1,3}))?$/.exec(q)
  if (m) return at(list.find((s) => s.n === Number(m![1])), m[2])
  m = /^(.*?[a-z].*?)\s*[:.\s]\s*(\d{1,3})$/.exec(q)
  if (m) return at(rankSurahs(list, m[1]!)[0], m[2])
  return null
}

// ---- search in the translation (GET /api/quran/cari) ----

export type SearchHit = { surah: number; ayah: number; nama: string; snippet: string; match: [number, number] }
type SearchPage = { ok: true; total: number; offset: number; hits: SearchHit[] }
/** Same rule as the server: at least 3 letters or digits. */
export const SEARCH_MIN = 3
export const searchable = (q: string) => norm(q).length >= SEARCH_MIN

export const useAyatSearch = (q: string) =>
  useInfiniteQuery({
    queryKey: ['quran-cari', q],
    queryFn: ({ pageParam }) => api<SearchPage>('GET', `/api/quran/cari?q=${encodeURIComponent(q)}${pageParam ? `&offset=${pageParam}` : ''}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.offset + last.hits.length < last.total ? last.offset + last.hits.length : undefined),
    enabled: searchable(q),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  })

// ---- "Baru dibuka" (this device only) ----

const RECENT_KEY = 'iqp_recent'
const RECENT_MAX = 5
export const recentSurahs = (): number[] => {
  try {
    const r = JSON.parse(safe.get(RECENT_KEY) ?? '[]') as unknown
    return Array.isArray(r) ? r.filter((n): n is number => Number.isInteger(n) && n >= 1 && n <= 114).slice(0, RECENT_MAX) : []
  } catch { return [] }
}
export const pushRecent = (n: number) => safe.set(RECENT_KEY, JSON.stringify([n, ...recentSurahs().filter((x) => x !== n)].slice(0, RECENT_MAX)))

// ---- "Tanda" (bookmarked verses, GET/POST /api/me/tanda) ----

export type Tanda = { surah: number; ayah: number; nama: string | null; snippet: string | null; savedAt: string }
export const TANDA_KEY = ['tanda']
export const useTanda = () => useQuery({ queryKey: TANDA_KEY, queryFn: () => api<{ ok: true; items: Tanda[] }>('GET', '/api/me/tanda'), select: (r) => r.items })
export const postTanda = (surah: number, ayah: number, on: boolean) => api<{ ok: true; on: boolean }>('POST', '/api/me/tanda', { surah, ayah, on })

/** Optimistic: the list changes at once, and goes back if the server says no. Returns the server error, if any. */
export async function toggleTanda(qc: QueryClient, t: { surah: number; ayah: number; nama: string | null; snippet?: string | null }, on: boolean) {
  const prev = qc.getQueryData<{ ok: true; items: Tanda[] }>(TANDA_KEY)
  const rest = (prev?.items ?? []).filter((x) => !(x.surah === t.surah && x.ayah === t.ayah))
  const items = on ? [{ surah: t.surah, ayah: t.ayah, nama: t.nama, snippet: t.snippet ?? null, savedAt: new Date().toISOString() }, ...rest] : rest
  qc.setQueryData(TANDA_KEY, { ok: true, items })
  try {
    await postTanda(t.surah, t.ayah, on)
    return null
  } catch (e) {
    qc.setQueryData(TANDA_KEY, prev)
    return e
  } finally {
    void qc.invalidateQueries({ queryKey: TANDA_KEY })
  }
}
